import { useMemo, useState, useEffect } from 'react';
import * as Constants from "../../constants";
import B2BApiService from "../../B2bApiService";
import { jwtDecode } from "jwt-decode";

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Modal from '@mui/material/Modal';
import InputError from "../../components/formComponents/InputError";
import WriteAccessGuard from "../reusableElementsComponents/WriteAccessGuard";
import checkGreen from '../../assets/images/icons/checkGreen.svg';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { TextField } from '@mui/material';
import loadingImage from '../../assets/images/icons/loading.gif';
import { t } from '../../content';

const MultiFactorAuthentication = ({userRole}) => {
    const authenticationData = JSON.parse(localStorage.getItem(Constants.MAZHR_JWT_KEY)) || null;

    const [isCompanyMFAEnabled, setCompanyMFAEnabled] = useState(false); // Company enforce MFA for all corporate users
    const [isUserMFAEnabled, setUserMFAEnabled] = useState(false); // Users activate/deactivate MFA for their own accounts
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmationType, setConfirmationType] = useState(null); // e.g., 'enableCompanyMFA', 'disableCompanyMFA', 'enableUserMFA', 'disableUserMFA', etc.
    const [showMFASetupModal, setShowMFASetupModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [mfaData, setMfaData] = useState(null);
    const [authCode, setAuthCode] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        // For integrity check, if decoded JWT is available, use the values from JWT
        if (authenticationData?.access_token) {
            const decodedJWT = jwtDecode(authenticationData?.access_token?.toString());
            setCompanyMFAEnabled(decodedJWT?.company_enforce_mfa || false);
            setUserMFAEnabled(decodedJWT?.mfa_activated || false);
        // If the JWT is not available, check the localStorage values
        } else if (authenticationData?.company_enforce_mfa && authenticationData?.mfa_activated) {
            setCompanyMFAEnabled(authenticationData?.company_enforce_mfa || false);
            setUserMFAEnabled(authenticationData?.mfa_activated || false);
        }
    }, [authenticationData]);

    /**
     * ALL FUNCTIONS FOR API CALLS
     */
    const b2bServiceObj = useMemo(() => {
        return new B2BApiService();
    }, []);

    const updateAuthenticationData = (newData = {}, mfaFlags = {}) => {
        const currentData = authenticationData || {};
        localStorage.setItem(Constants.MAZHR_JWT_KEY, JSON.stringify({
            ...currentData,
            ...mfaFlags,
            access_token: newData.access_token || currentData.access_token,
            refresh_token: newData.refresh_token || currentData.refresh_token,
        }));
    };

    const enableCompanyMFA = async (callback) => {
        setLoading(true);
        try {
            const response = await b2bServiceObj.EnforceCompanyMFA();
            if (response.httpStatus === 200) {
                if (response?.json) {
                updateAuthenticationData(response.json, {
                    company_enforce_mfa: true,
                });
                }
                callback && callback(); // callback function to update UI
                setError(null);
            } else {
                setError(t('auth.mfa.errors.enforceCompany'));
            }
        } catch (error) {
            console.error('Error enforcing company MFA:', error);
            setError(t('auth.mfa.errors.enforceCompany'));
        } finally {
            setLoading(false);
        }
    };      

    const disableCompanyMFA = async (callback) => {
        setLoading(true);
        setError(null);
        try {
            const response = await b2bServiceObj.DisableCompanyMFA();
            if (response.httpStatus === 200) {
                if (response?.json) {
                    updateAuthenticationData(response.json, {
                        company_enforce_mfa: false,
                    });
                }
                callback && callback(); // callback function to update UI
                setError(null);
            } else {
                setError(t('auth.mfa.errors.disableCompany'));
            }
        } catch (error) {
            console.error('Error disabling company MFA:', error);
            setError(t('auth.mfa.errors.disableCompany'));
        } finally {
            setLoading(false);
        }
    }

    const activateUserMFA = async (callback) => {
        setLoading(true);
        setError(null);
        try {
            const response = await b2bServiceObj.ActivateUserMFA();
            if (response.httpStatus === 200) {
                if (response?.json) {
                    setMfaData(response.json);
                    updateAuthenticationData(response.json, {
                        mfa_activated: true,
                    });
                }
                callback && callback(); // callback function to update UI
                setError(null);
            } else {
                setError(t('auth.mfa.errors.activateUser'));
            }
        } catch (error) {
            console.error('Error activating user MFA:', error);
            setError(t('auth.mfa.errors.activateUser'));
        }
        finally {
            setLoading(false);
        }
    };

    const verifyAuthCode = async (callback) => {
        if (!authCode?.trim()) {
          setError(t('validation.codeEmpty'));
          return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = { mfa_code: authCode };
            const response = await b2bServiceObj.VerifyMFA(data);
        
            if (response.httpStatus === 200) {
                if (response?.json) {
                    updateAuthenticationData(response.json, {
                        mfa_verified: true,
                    });
                }
                callback && callback(); // callback function to update UI
                setError(null);
            } else {
                setError(t('validation.incorrectCode'));
            }
        } catch (error) {
          console.error('Error verifying MFA code:', error);
          setError(t('auth.mfa.errors.verify'));
        } finally {
          setLoading(false);
          setAuthCode('');
        }
    };

    const deactivateUserMFA = async (callback) => {
        setLoading(true);
        try {
            const response = await b2bServiceObj.DeactivateUserMFA();
            if (response.httpStatus === 200) {
                if (response?.json) {
                    updateAuthenticationData(response.json, {
                        mfa_activated: false,
                        mfa_verified: false,
                    });
                }
                callback && callback(); // callback function to update UI
                setError(null);
            } else {
                setError(t('auth.mfa.errors.deactivateUser'));
            }
        } catch (error) {
            console.error('Error deactivating user MFA:', error);
            setError(t('auth.mfa.errors.deactivateUser'));
        } finally {
            setLoading(false);
        }
    }
    
    /**
     * ALL FUNCTIONS FOR TOGGLING CHOICES AND CONFIRMATION MODAL
     */
    const handleCompanyMFAToggle = () => {
        if (!isCompanyMFAEnabled) {
            setConfirmationType("enableCompanyMFA");
        } else {
            setConfirmationType("disableCompanyMFA");
        }
        setShowConfirmationModal(true);
    };
    
    const handleUserMFAToggle = () => {
        if (!isUserMFAEnabled) {
            setConfirmationType("enableUserMFA");
        } else {
            setConfirmationType("disableUserMFA");
        }
        setShowConfirmationModal(true);
    };
    
    // This onConfirm function is strictly for the confirmation modal only
    const onConfirm = () => {
        if (confirmationType === "enableCompanyMFA") {
            enableCompanyMFA(() => {
                setShowConfirmationModal(false);
                if (!isUserMFAEnabled) {
                    // Begin MFA setup process
                    setShowMFASetupModal(true);
                }
            });
            
        } else if (confirmationType === "disableCompanyMFA") {
            disableCompanyMFA(() => {
                setShowConfirmationModal(false);
            });
        } else if (confirmationType === "enableUserMFA") {
            activateUserMFA(() => {
                setShowConfirmationModal(false);
                setShowMFASetupModal(true);
            });
        } else if (confirmationType === "disableUserMFA") {
            deactivateUserMFA(() => {
                setShowConfirmationModal(false);
            });
        }
        
    };
    
    return (
        <>
        <div className='change-password-section wrapper-div'>
            <div className="change-password-inner-div">
                <h4>{t('auth.mfa.title')}</h4>

                {Constants.ADMINISTRATIVE_USER_ROLES.includes(userRole) && (
                    <div className="mar-25 top">
                        <label htmlFor="Enforce Multifactor Authentication">{t('auth.mfa.enforceCompany')}</label>
                        <Stack direction="row" spacing={1} alignItems="center">
                        <Typography><b>{t('common.labels.off')}</b></Typography>
                        <WriteAccessGuard>
                            <Switch
                                checked={isCompanyMFAEnabled}
                                onChange={handleCompanyMFAToggle}
                                inputProps={{ 'aria-label': 'controlled' }}
                            />
                        </WriteAccessGuard>
                        <Typography><b>{t('common.labels.on')}</b></Typography>
                        </Stack>
                    </div>
                )}

                <div className="mar-25 top">
    
                    {isCompanyMFAEnabled && !Constants.ADMINISTRATIVE_USER_ROLES.includes(userRole) ? (
                        <p>
                            <strong>{t('auth.mfa.companyRequires')}</strong><br/>
                            {t('auth.mfa.cannotTurnOff')}
                        </p>
                    ) : (
                        <label htmlFor="Enforce Multifactor Authentication">{t('auth.mfa.toggleOwnAccount')}</label>
                    )}
                    <Stack direction="row" spacing={1} alignItems="center">
                    <Typography><b>{t('auth.mfa.deactivate')}</b></Typography>
                    <WriteAccessGuard>
                        <Switch
                            checked={isUserMFAEnabled}
                            onChange={handleUserMFAToggle}
                            inputProps={{ 'aria-label': 'controlled' }}
                            disabled={isCompanyMFAEnabled}
                        />
                    </WriteAccessGuard>
                    <Typography><b>{t('auth.mfa.activate')}</b></Typography>
                    </Stack>
                </div>
            </div>
        </div>

        {/* {showConfirmationModal && ( */}
            <MFAConfirmationModal
                loading={loading}
                open={showConfirmationModal}
                type={confirmationType}
                isUserMFAEnabled={isUserMFAEnabled}
                onClose={() => setShowConfirmationModal(false)}
                onConfirm={onConfirm}
            />
        {/* )} */}
        {/* {showMFASetupModal && ( */}
            <MFASetupModal
                loading={loading}
                open={showMFASetupModal}
                onClose={() => setShowMFASetupModal(false)}
                activateUserMFA={activateUserMFA}
                qr_code={mfaData?.qr_code || null}
                mfa_secret={mfaData?.mfa_secret || null}
                authCode={authCode}
                setAuthCode={setAuthCode}
                verifyAuthCode={verifyAuthCode}
                error={error}
                setError={setError}
            />
        {/* )} */}

        </>
    );
}

const MFAConfirmationModal = ({ loading, open, onClose, type, isUserMFAEnabled, onConfirm }) => {

    let message = "";
    if (type === "enableCompanyMFA") {
        message = (<>
            <span style={{ color: 'red' }}>
            {t('auth.mfa.confirmEnableCompanyNote')}
            </span>
            <br />
            {t('auth.mfa.confirmEnableCompanyBody')}
            <br />
            {t('auth.mfa.confirmEnableCompanyAlready')}
            <br />
            <span style={{ color: 'red' }}>
            {isUserMFAEnabled
                ? t('auth.mfa.alreadyActivatedOwn')
                : t('auth.mfa.notYetActivatedOwn')}
            </span>
        </>);
    } else if (type === "disableCompanyMFA") {
        message = (<>
            <span style={{ color: 'red' }}>
            {t('auth.mfa.confirmDisableCompanyNote')}
            </span>
            <br />
            {t('auth.mfa.confirmDisableCompanyBody')}
        </>);
    } else if (type === "enableUserMFA") {
        message = (<>{t('auth.mfa.confirmEnableUser')}</>);
    } else if (type === "disableUserMFA") {
        message = (<>{t('auth.mfa.confirmDisableUser')}</>);
    }
    
    return (
        <Modal
            open={open}
            title={t('auth.mfa.title')}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            slotProps={{
                backdrop: {
                    sx: {
                        height: "100vh",
                    },
                },
            }}
        >
            <div style={{ 
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'fit-content',
                minWidth: '500px',
                height: 'fit-content',
                minHeight: '500px',
                backgroundColor: 'white',
                padding: '30px',
                textAlign: 'center',
                alignContent: 'center',
                // alignItems: 'center',
                borderRadius: '10px',
             }}>
                {loading ? <img src={loadingImage} alt={t('common.labels.loading')} /> : <>
                    <p>{message}</p>
                    <div  className="flex justify-content-center">
                        <button className="mazhrBtn default-green white mar-25 right" onClick={onConfirm}>{t('common.actions.confirm')}</button>
                        <button  className="mazhrBtn light-grey white" onClick={onClose}>{t('common.actions.cancel')}</button>
                    </div>
                </>
                }
            </div>
        </Modal>
    )
}

function MFASetupModal({ 
    loading,
    open, 
    onClose, 
    activateUserMFA,
    qr_code, 
    mfa_secret, 
    authCode, 
    setAuthCode,
    verifyAuthCode, 
    error ,
    setError,
}) {
    const [step, setStep] = useState(1); // Tracks the current step in the MFA process
    // const [authCode, setAuthCode] = useState('');
    // const [verificationError, setVerificationError] = useState(null);
  
    useEffect(() => {
        if (open) {
            setStep(1);
            activateUserMFA();
        }
        // eslint-disable-next-line
    }, [open]); // Omit activateUserMFA from the dependency array to avoid infinite loop
  
    const handleVerificationChange = (e) => {
        const value = e.target.value;
        setAuthCode(value);
    
        // Remove error if user starts typing
        if (value.trim() === '') {
            setError(t('validation.codeEmpty'));
        } else {
            setError(null);
        }
    };

    // Handle Enter key press when users verify the MFA code
    const handleVerificationKeyDown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();  // Prevent form submission or default behavior
          verifyAuthCode(() => setStep(3));
        }
    };
  
    return (
        <Modal
            open={open}
            title={t('auth.mfa.title')}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            slotProps={{
                backdrop: {
                    sx: {
                        height: "100vh",
                    },
                },
            }}
        >
            <div style={{ 
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'fit-content',
                minWidth: '500px',
                height: 'fit-content',
                minHeight: '500px',
                backgroundColor: 'white',
                padding: '30px',
                textAlign: 'center',
                alignContent: 'center',
                // alignItems: 'center',
                borderRadius: '10px',
            }}>
                {loading ? <img src={loadingImage} alt={t('common.labels.loading')} /> 
                : <>
                    {error && <InputError errorMessage={error} />}
                    {step === 1 && (
                        <>
                        <h2>{t('auth.mfa.setupTitle')}</h2>
                        <p><span style={{color: 'red', fontWeight: 'bold'}}>
                            {t('auth.mfa.doNotClose')}</span><br/>
                            {t('auth.mfa.contactIfIssue')}
                        </p>
                        <p>{t('auth.mfa.installRecommend')}<br/>
                        <b>
                            {t('auth.mfa.googleAuthenticatorFor')} <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer"> {t('auth.mfa.android')} </a> {t('auth.mfa.or')} 
                            <a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank" rel="noopener noreferrer"> {t('auth.mfa.ios')}</a>
                        <br/>
                            {t('auth.mfa.microsoftAuthenticatorFor')} <a href="https://play.google.com/store/apps/details?id=com.azure.authenticator" target="_blank" rel="noopener noreferrer"> {t('auth.mfa.android')}</a> {t('auth.mfa.or')} 
                            <a href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458" target="_blank" rel="noopener noreferrer"> {t('auth.mfa.ios')}</a>
                        <br/>
                            {t('auth.mfa.authyAuthenticatorFor')} <a href="https://play.google.com/store/apps/details?id=com.authy.authy" target="_blank" rel="noopener noreferrer">{t('auth.mfa.android')}</a> {t('auth.mfa.or')} 
                            <a href="https://apps.apple.com/us/app/authy/id494168017" target="_blank" rel="noopener noreferrer"> {t('auth.mfa.ios')}</a>
                        </b></p>
                        
                        <p>
                            {t('auth.mfa.openApp')}<br/>
                            {t('auth.mfa.holdPhone')}<br/>
                            <strong>{t('auth.mfa.doNotUseCameraPrefix')}</strong> {t('auth.mfa.doNotUseCameraRest')} <br/>
                            {t('auth.mfa.useAuthenticatorPrefix')} <strong>{t('auth.mfa.authenticatorApp')}</strong> {t('auth.mfa.useAuthenticatorRest')}
                        </p>
                        <p style={{color: 'red', fontWeight: 'bold'}}>
                            {t('auth.mfa.willNotShowAgain')}<br/>
                            {t('auth.mfa.saveQrLater')}
                        </p>
                        {qr_code ? 
                            <img src={`data:image/png;base64,${qr_code}`} alt={t('auth.mfa.qrCodeAlt')} height={400}/> : 
                            <InputError errorMessage={t('auth.mfa.qrError')} />
                        }

                        {mfa_secret && (
                            <p>{t('auth.mfa.enterSetupKey')} <strong>{mfa_secret.match(/.{1,4}/g)?.join(' ') || mfa_secret}</strong></p>
                        )}

                        <button className="mazhrBtn default-green mar-20 top full-width" onClick={() => setStep(2)}>{t('auth.mfa.doneSetup')}</button>
                        
                        </>
                    )}

                    {step === 2 && (
                        <>
                        <h2>{t('auth.mfa.verificationTitle')}</h2>
                        <p>
                            {t('auth.mfa.accountProtected')}<br/>
                            {t('auth.mfa.enterCodeFromApp')}<br/>
                            {t('auth.mfa.contactIfIssue')}
                        </p>
                        <TextField
                            fullWidth
                            placeholder={t('common.placeholders.enterCode')}
                            label={t('auth.mfa.authCode')} 
                            variant="standard"
                            name="authCode"
                            type="authCode"
                            autoComplete="on"
                            value={authCode}
                            inputProps={{style: { textAlign: 'center' }}}
                            onKeyDown={handleVerificationKeyDown}
                            onChange={handleVerificationChange}
                        />
                        <button className="mazhrBtn default-green mar-20 top full-width" onClick={() => verifyAuthCode(() => setStep(3))}>{t('common.actions.verify')}</button>
                        
                        <button className="mazhrBtn default-pink mar-20 top full-width" 
                            onClick={() => window.location.href = `mailto:security@apexteam.ai`} >
                            {t('auth.mfa.contactSupport')}
                        </button>
                        </>
                    )}
                    {step === 3 && (
                        <>
                        <div>
                            {CheckCircleOutlinedIcon 
                                ? <CheckCircleOutlinedIcon style={{ color: 'green', fontSize: 100 }} /> 
                                : <img src={checkGreen || ''} height={60} alt="verification_success" />
                            }
                        </div>
                        <h2>{t('auth.mfa.succeeded')}</h2>
                        <button className="mazhrBtn default-green mar-20 top full-width" onClick={onClose}>
                            {t('auth.mfa.backToSettings')}
                        </button>
                        </>
                    )}
                </>}
            </div>
        </Modal>
    );
  }
 
export default MultiFactorAuthentication;